import React from 'react';
import { CheckCircle, MapPin, Building, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const WestToEastPage = () => {
  const disciplines = [
    'Process Engineering',
    'Piping Engineering',
    'Electrical Engineering',
    'Instrumentation & Control Engineering',
    'Civil & Structural Engineering'
  ];

  const keyStats = [
    { value: '9', label: 'Surge Relief Valve Skids' },
    { value: '5', label: 'Engineering Disciplines' },
    { value: 'Abu Dhabi', label: 'Project Location' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_precision-vision/artifacts/hmbt43dh_WEP.jpg)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            West To East Pipeline Project
          </h1>
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">Abu Dhabi, UAE</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              <span className="text-lg">ADNOC</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="text-lg">2025 - Till Now</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Project <span className="text-red-600">Overview</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              The objective of this Scope of Work is to define the CONSULTANT's responsibilities for the 
              design, engineering, and supply of deliverables for nine (9) Surge Relief Valve (SRV) skids, 
              complete with associated accessories.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {keyStats.map((stat, index) => (
              <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">{stat.value}</div>
                  <div className="text-red-100">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Project Images */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <Card className="border-none shadow-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1698031610524-c35e7ebbba2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxwaXBlbGluZSUyMHZhbHZlJTIwaW5kdXN0cmlhbCUyMG9pbCUyMGdhcyUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc3MjU0MjgzN3ww&ixlib=rb-4.1.0&q=85"
                alt="Industrial pipeline valves"
                className="w-full h-80 object-cover"
              />
            </Card>
            <Card className="border-none shadow-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1743580886673-812abb5acf3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxwaXBlbGluZSUyMHZhbHZlJTIwaW5kdXN0cmlhbCUyMG9pbCUyMGdhcyUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc3MjU0MjgzN3ww&ixlib=rb-4.1.0&q=85"
                alt="Pipeline system with valves and tanks"
                className="w-full h-80 object-cover"
              />
            </Card>
          </div>

          {/* Detailed Engineering Services */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Detailed Engineering Services
            </h3>
            <p className="text-slate-700 mb-6">
              Total Vision provides comprehensive engineering services across multiple disciplines for this critical pipeline infrastructure project:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {disciplines.map((discipline, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{discipline}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-6">
                Surge Relief Valve (SRV) Skids
              </h3>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                The West To East Pipeline Project represents a critical infrastructure development for ADNOC's 
                pipeline network across the UAE. Our comprehensive engineering services focus on the design, 
                engineering, and supply of deliverables for nine specialized Surge Relief Valve (SRV) skids.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                These SRV skids, complete with all associated accessories, are essential safety components that 
                protect the pipeline system from pressure surges and ensure optimal operational performance 
                throughout the West To East pipeline corridor.
              </p>
            </div>
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4">Project Scope</h4>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Design and engineering of 9 SRV skids</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Complete deliverables with associated accessories</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Multidisciplinary detailed engineering services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Process and piping engineering integration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Electrical and instrumentation systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Civil and structural engineering support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Comprehensive control and monitoring systems</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Project Significance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Project Significance</h3>
            <p className="text-lg text-slate-700 leading-relaxed">
              The West To East Pipeline Project plays a vital role in ADNOC's strategic infrastructure network, 
              facilitating the secure and efficient transportation of hydrocarbons across the UAE. The integration 
              of advanced Surge Relief Valve skids ensures operational safety and system reliability, protecting 
              critical pipeline assets while maintaining continuous flow operations. This project demonstrates 
              Total Vision's expertise in delivering specialized engineering solutions for complex pipeline 
              infrastructure.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WestToEastPage;
